/**
 * 퇴사 타임라인 — 퇴사일을 넣으면 돈이 걸린 마감이 D-day 로 깔린다.
 *
 * 수익 라인이 아니라 트래픽 자산이다
 * ([계획](../../../../docs/ideas-2026-08/plans/resignation-timeline.md)).
 * 그래서 이 페이지의 신뢰 조건: 로그인 없음 · 서버 전송 없음(입력은 localStorage 에만) ·
 * 확정 판정 없음(안내 + 기관 링크까지).
 */
import { useEffect, useMemo, useState } from 'react'
import { Badge, Button, Card, Container, Text } from '@cottonray/ui-web'
import { buildIcs, buildTimeline, daysBetween, type ResignationInput } from './resignationRules'

const STORAGE_KEY = 'resignation-timeline-v1'

// insuredMonths 는 아래 select 의 option 값 중 하나여야 한다 — 어긋나면 브라우저가
// 첫 옵션을 그려서 화면과 상태가 다른 값을 가리킨다
const DEFAULTS: ResignationInput = {
  leaveDate: '',
  voluntary: false,
  insuredMonths: 24,
  age: 34,
  hasSeverance: true,
}

function load(): ResignationInput {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return DEFAULTS
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<ResignationInput>) }
  } catch {
    return DEFAULTS
  }
}

export function ResignationTimeline() {
  const [input, setInput] = useState<ResignationInput>(load)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(input))
    } catch {
      /* 시크릿 모드 등 — 저장 실패는 기능 저하로만 둔다 */
    }
  }, [input])

  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const cards = useMemo(
    () => (input.leaveDate === '' ? [] : buildTimeline(input)),
    [input],
  )

  const set = <K extends keyof ResignationInput>(key: K, value: ResignationInput[K]) =>
    setInput((prev) => ({ ...prev, [key]: value }))

  const downloadIcs = () => {
    const blob = new Blob([buildIcs(cards, today)], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'resignation-timeline.ics'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="rt">
      <section className="rt-hero">
        <Container>
          <Badge tone="accent">Free · no sign-in</Badge>
          <Text variant="display" className="rt-hero__title">
            Resignation Timeline
          </Text>
          <Text tone="muted" className="rt-hero__lead">
            Leaving a job in Korea starts several clocks at once — unemployment benefit, health
            insurance, severance, tax. Give us your last working day and we lay out the deadlines
            that have money attached as D-days. What you enter stays in this browser and is never
            sent to a server.
          </Text>
        </Container>
      </section>

      <section className="band band--surface">
        <Container>
          <Card className="rt-form">
            <div className="rt-form__grid">
              <label className="rt-field">
                <Text variant="label">Last working day</Text>
                <input
                  type="date"
                  value={input.leaveDate}
                  onChange={(e) => set('leaveDate', e.target.value)}
                />
              </label>

              <div className="rt-field">
                <Text variant="label">Reason for leaving</Text>
                <div className="rt-seg" role="radiogroup" aria-label="Reason for leaving">
                  <button
                    type="button"
                    className={input.voluntary ? '' : 'is-on'}
                    onClick={() => set('voluntary', false)}
                  >
                    Laid off, contract ended, etc.
                  </button>
                  <button
                    type="button"
                    className={input.voluntary ? 'is-on' : ''}
                    onClick={() => set('voluntary', true)}
                  >
                    Resigned voluntarily
                  </button>
                </div>
              </div>

              <label className="rt-field">
                <Text variant="label">Employment insurance period</Text>
                <select
                  value={String(input.insuredMonths)}
                  onChange={(e) => set('insuredMonths', Number(e.target.value))}
                >
                  <option value="6">Under 1 year</option>
                  <option value="24">1 to 3 years</option>
                  <option value="48">3 to 5 years</option>
                  <option value="84">5 to 10 years</option>
                  <option value="150">10 years or more</option>
                </select>
              </label>

              <label className="rt-field">
                <Text variant="label">Age</Text>
                <input
                  type="number"
                  min={15}
                  max={80}
                  value={input.age}
                  onChange={(e) => set('age', Number(e.target.value))}
                />
              </label>

              <label className="rt-field rt-field--check">
                <input
                  type="checkbox"
                  checked={input.hasSeverance}
                  onChange={(e) => set('hasSeverance', e.target.checked)}
                />
                <Text>Eligible for severance (one year or more at the same employer)</Text>
              </label>
            </div>
          </Card>

          {cards.length === 0 ? (
            <Text tone="muted" className="rt-empty">
              Enter your last working day and the timeline appears.
            </Text>
          ) : (
            <>
              <div className="rt-cards">
                {cards.map((card) => (
                  <Card key={card.id} className={`rt-card rt-card--${card.applies}`}>
                    <div className="rt-card__head">
                      <Text variant="title" className="rt-card__title">
                        {card.title}
                      </Text>
                      <Badge tone={card.applies === 'yes' ? 'accent' : 'neutral'}>
                        {card.applies === 'yes'
                          ? 'Applies'
                          : card.applies === 'conditional'
                            ? 'Check conditions'
                            : 'Does not apply'}
                      </Badge>
                    </div>
                    {card.date !== undefined && card.applies !== 'no' && (
                      <Text variant="title" className="rt-card__dday">
                        {card.date} {card.dateKind}
                        <span className="rt-card__badge">{ddayLabel(today, card.date)}</span>
                      </Text>
                    )}
                    <ul className="rt-card__lines">
                      {card.lines.map((line) => (
                        <li key={line}>
                          <Text tone="muted">{line}</Text>
                        </li>
                      ))}
                    </ul>
                    <a href={card.source.url} target="_blank" rel="noreferrer" className="rt-card__source">
                      {card.source.label} ↗
                    </a>
                  </Card>
                ))}
              </div>

              <div className="rt-actions">
                <Button onClick={downloadIcs}>Add to calendar (.ics)</Button>
                <Text tone="muted" className="rt-actions__hint">
                  Open the downloaded file and each deadline, plus a reminder seven days ahead,
                  lands in the calendar you already use.
                </Text>
              </div>
            </>
          )}

          <Text tone="muted" className="rt-disclaimer">
            This page is general information, not legal or tax advice. Amounts and eligibility are
            decided by the bodies concerned — the employment centre, the National Health Insurance
            Service, the National Pension Service, and the National Tax Service — and the rules can
            change.
          </Text>
        </Container>
      </section>
    </div>
  )
}

function ddayLabel(today: string, date: string): string {
  const d = daysBetween(today, date)
  if (d === 0) return 'D-day'
  return d > 0 ? `D-${d}` : `passed (+${-d} days)`
}
